import { useRef, useEffect, useCallback } from 'react';
import type { ChatMessage } from '@mem-ai-hub/shared';
import MessageBubble from './MessageBubble';
import ThinkingIndicator from './ThinkingIndicator';

interface ChatWindowProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingContent: string;
  thinkingContent: string;
  compact?: boolean;
}

export default function ChatWindow({
  messages,
  isStreaming,
  streamingContent,
  thinkingContent,
  compact = false,
}: ChatWindowProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevMessagesLengthRef = useRef<number>(messages.length);

  // Detect when user scrolls away from bottom
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 100;
  }, []);

  // Reset scroll lock when a new user message is added (new send)
  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      userScrolledUpRef.current = false;
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length]);

  // Throttled auto-scroll: only if user hasn't scrolled up
  useEffect(() => {
    if (userScrolledUpRef.current) return;
    if (scrollTimerRef.current) return;
    scrollTimerRef.current = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      scrollTimerRef.current = null;
    }, 150);
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };
  }, [messages.length, streamingContent, thinkingContent]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <svg
            className="mx-auto mb-4 h-12 w-12 text-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-text-muted font-body text-sm">
            Start a conversation by typing a message below
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className={`flex min-h-0 flex-1 flex-col overflow-y-auto p-4 ${compact ? 'gap-2' : 'gap-4'}`}
    >
      {messages.map((msg) => (
        <MessageBubble
          key={msg.id}
          message={msg}
          isStreaming={false}
          streamingContent={undefined}
          thinkingContent={undefined}
          compact={compact}
        />
      ))}

      {/* Streaming assistant response (not yet in messages array) */}
      {isStreaming && (
        <>
          {!streamingContent && !thinkingContent ? (
            <div className="flex justify-start">
              <div className="max-w-[85%]">
                <ThinkingIndicator />
              </div>
            </div>
          ) : (
            <MessageBubble
              message={{
                id: 'streaming',
                sessionId: '',
                role: 'assistant',
                content: '',
                timestamp: new Date().toISOString(),
              }}
              isStreaming={true}
              streamingContent={streamingContent}
              thinkingContent={thinkingContent}
              compact={compact}
            />
          )}
        </>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
